import React from 'react';
import { Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { ReportData } from '../../../types/report.types';

interface ConclusionToolsPageProps {
  reportData: ReportData;
  sectionId?: string;
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: '16mm 14mm',
    paddingBottom: '22mm',
    fontFamily: 'Roboto',
    fontSize: 9.5,
    color: '#4B5563',
    position: 'relative',
  },

  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0F172A',
  },
  titleUnderlineRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    width: '100%',
    height: 3,
    marginTop: 4,
    marginBottom: 16,
  },
  titleUnderlineBlue: {
    width: 60,
    height: 3,
    backgroundColor: '#2563EB',
  },
  titleUnderlineDark: {
    flex: 1,
    height: 1.2,
    backgroundColor: '#1E293B',
  },
  paragraph: {
    lineHeight: 1.60,
    marginBottom: 18,
    color: '#475569',
    fontSize: 11.5,
    textAlign: 'justify',
  },
  blueSubtitle: {
    fontSize: 9.5,
    fontWeight: 'bold',
    color: '#2563EB',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 8,
    marginBottom: 8,
  },
  table: {
    borderWidth: 1.2,
    borderColor: '#CBD5E1',
    backgroundColor: '#FFFFFF',
    marginTop: 8,
    marginBottom: 16,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1.8,
    borderBottomColor: '#475569',
  },
  tableHeaderCell: {
    color: '#4B5563',
    fontWeight: 'bold',
    fontSize: 9.0,
    paddingHorizontal: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    minHeight: 32,
    alignItems: 'center',
  },
  tableRowCell: {
    fontSize: 10.0,
    paddingHorizontal: 10,
    paddingVertical: 8,
    color: '#374151',
    lineHeight: 1.45,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    fontSize: 8.5,
    color: '#9CA3AF',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 14,
  },
  footerLeft: {
    textAlign: 'left',
    flex: 1,
  },
  footerRight: {
    textAlign: 'right',
    flex: 1,
  },
  bulletRow: {
    flexDirection: 'row',
    marginBottom: 6,
    paddingLeft: 4,
  },
  bulletDot: {
    width: 10,
    fontSize: 11.5,
    color: '#4B5563',
  },
  bulletText: {
    flex: 1,
    fontSize: 11.5,
    color: '#1F2937',
    lineHeight: 1.60,
  },
});

const ConclusionToolsPageV2: React.FC<ConclusionToolsPageProps> = ({ reportData, sectionId }) => {
  let parsedConfig: any = {};
  if (reportData.finding_config) {
    if (typeof reportData.finding_config === 'string') {
      try {
        parsedConfig = JSON.parse(reportData.finding_config);
      } catch (_) {}
    } else {
      parsedConfig = reportData.finding_config;
    }
  }
  const conclusionConfig = parsedConfig?.conclusionV2 || {};

  // Resolve client and scope details dynamically to avoid hardcoded mock data
  const clientName = reportData.company_name || 'the client';
  const targetName = reportData.program_details?.[0]?.program_name || reportData.target_details?.[0]?.target_value || 'the target application';
  
  const targets = reportData.target_details || [];
  const isNetworkTarget = (t: any) => {
    const type = (t.target_type || t.type || '').toLowerCase();
    const value = (t.target_value || t.url || t.target_name || '').toLowerCase();
    if (type.includes('web') || type.includes('api') || type.includes('app') || type.includes('mobile') || type.includes('ios') || type.includes('android')) {
      return false;
    }
    if (value.startsWith('http://') || value.startsWith('https://')) {
      return false;
    }
    return type.includes('network') || type.includes('infra') || type.includes('subnet') || type.includes('range') || type.includes('ip') ||
           !!value.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/) ||
           /\/\s*\d{1,2}\s*$/.test(value);
  };

  const networkTargets = targets.filter(isNetworkTarget);
  const webTargets = targets.filter(t => !isNetworkTarget(t));
  const isNetwork = reportData.testing_type?.toLowerCase().includes('network') ||
    reportData.program_details?.[0]?.testing_type?.toLowerCase().includes('network') || (networkTargets.length > 0 && webTargets.length === 0);

  const allFindings = reportData.detailed_findings || [];

  // Build dynamic default text from actual findings to avoid static placeholders
  const buildDynamicConclusion = (): string => {
    const sevOrder: Record<string, number> = { critical: 1, high: 2, medium: 3, low: 4, info: 5 };
    const topFindings = [...allFindings]
      .filter((f: any) => ['critical', 'high'].includes((f.severity_category || f.severity || '').toLowerCase()))
      .sort((a, b) => {
        const aS = (a.severity_category || a.severity || '').toLowerCase();
        const bS = (b.severity_category || b.severity || '').toLowerCase();
        return (sevOrder[aS] || 99) - (sevOrder[bS] || 99);
      });

    const branding = reportData.branding_company || (reportData as any).organization_name || 'Our testing team';

    if (isNetwork) {
      if (topFindings.length > 0) {
        const titles = topFindings.slice(0, 2).map((f: any) => f.title).join(' and ');
        return `The internal network security posture of ${targetName} has room for improvement. Testing demonstrated that ${titles} could be leveraged to escalate network privileges and gain unauthorised access to sensitive systems. Prioritising remediation of critical and high findings will substantially reduce overall risk. ${branding} recommends a retest of all remediated items to validate the fixes.`;
      }
      return `The internal network security posture of ${targetName} has room for improvement. Prioritising remediation of critical and high findings will substantially reduce overall risk. ${branding} recommends a retest of all remediated items to validate the fixes.`;
    }

    if (topFindings.length > 0) {
      const topTitle = topFindings[0].title || 'the most critical vulnerability';
      const rest = topFindings.slice(1, 3).map((f: any) => f.title).filter(Boolean);
      const restText = rest.length > 0 ? `, alongside ${rest.join(' and ')},` : '';
      return `Overall, the security posture of ${targetName} requires immediate attention. ${topTitle}${restText} ${topFindings.length === 1 ? 'was' : 'were'} identified as the most impactful issue${topFindings.length > 1 ? 's' : ''}, posing a direct risk to the confidentiality, integrity, and availability of the application. Prioritising remediation of critical and high severity findings will substantially reduce overall risk. ${branding} recommends a retest of all remediated items to validate the fixes.`;
    }

    return `Overall, the security posture of ${targetName} is adequate. No critical or high severity issues were identified during the testing window. ${branding} recommends periodic retesting to maintain this posture.`;
  };

  const MOCK_CONCLUSION_MARKERS = [
    'input validation flaws and access control',
    'weaknesses in endpoint access validation',
    'The assessment revealed areas of security concern',
    'stem from input validation flaws and access control bugs',
    'hash-dumping or lateral credential reuse'
  ];
  const isMockConclusion = (text: string): boolean => {
    if (!text) return true;
    const lower = text.toLowerCase();
    return MOCK_CONCLUSION_MARKERS.some(marker => lower.includes(marker.toLowerCase()));
  };

  const hasCustomConclusion = reportData.conclusion && !isMockConclusion(reportData.conclusion);

  const conclusionText = hasCustomConclusion
    ? reportData.conclusion!.replace(/<[^>]*>/g, '')
    : buildDynamicConclusion();

  // Resolve Tools and Approach data
  const defaultWebTools = [
    { name: 'Burp Suite Professional', purpose: 'Intercepting proxy, manual testing & scanning' },
    { name: 'OWASP ZAP', purpose: 'Automated scanning & spidering' },
    { name: 'sqlmap', purpose: 'SQL injection detection & exploitation' },
    { name: 'ffuf / dirsearch', purpose: 'Content & directory discovery' },
    { name: 'nuclei / nikto', purpose: 'Known-vulnerability & misconfiguration checks' },
    { name: 'jwt_tool', purpose: 'JWT analysis & tampering' },
    { name: 'Postman', purpose: 'API request crafting & testing' },
    { name: 'Nessus', purpose: 'Authenticated vulnerability scanning' }
  ];

  const defaultNetworkTools = [
    { name: 'Nmap', purpose: 'Host discovery, port & service enumeration' },
    { name: 'Nessus', purpose: 'Authenticated vulnerability scanning' },
    { name: 'CrackMapExec', purpose: 'SMB enumeration, pass-the-hash, lateral movement' },
    { name: 'Impacket (ntlmrelayx, GetUserSPNs, secretsdump)', purpose: 'NTLM relay, Kerberoasting, credential dumping' },
    { name: 'Responder', purpose: 'LLMNR/NBT-NS poisoning' },
    { name: 'BloodHound', purpose: 'Active Directory attack-path analysis' },
    { name: 'Metasploit', purpose: 'Exploitation & validation' },
    { name: 'hashcat', purpose: 'Offline password / ticket cracking' }
  ];

  const toolsConfig = parsedConfig?.toolsApproach || {};
  const defaultTools = isNetwork ? defaultNetworkTools : defaultWebTools;
  const tools = Array.isArray(toolsConfig.tools) ? toolsConfig.tools : (parsedConfig?.toolsUsed || defaultTools);

  const defaultWebApproach = `The assessment combined automated scanning with in-depth manual testing. Automated tools provided broad coverage and initial discovery; every candidate issue was then manually verified and safely exploited to confirm real-world impact and eliminate false positives. Testing was performed from both unauthenticated and authenticated perspectives across the provided user roles, following the OWASP WSTG and mapped to the OWASP Top 10 (2021).`;
  
  const defaultNetworkApproach = `Testing was performed from an assumed-breach position on the corporate LAN, combining automated discovery and scanning with in-depth manual exploitation. Every issue was manually verified to confirm impact and remove false positives; destructive techniques were avoided.`;

  const defaultApproach = isNetwork ? defaultNetworkApproach : defaultWebApproach;
  const approachText = toolsConfig.approach !== undefined ? toolsConfig.approach : defaultApproach;

  return (
    <Page size="A4" id={sectionId} style={styles.page}>
      {/* Dynamic Section Heading: 8. Conclusion for network, 9 for web */}
      <Text style={styles.sectionTitle}>{isNetwork ? '8. Conclusion' : '9. Conclusion'}</Text>
      <View style={styles.titleUnderlineRow}>
        <View style={styles.titleUnderlineBlue} />
        <View style={styles.titleUnderlineDark} />
      </View>
      <Text style={styles.paragraph}>
        {conclusionText}
      </Text>

      {/* Dynamic Section Heading: 9. Tools & Approach for network, 10 for web */}
      <Text style={[styles.sectionTitle, { marginTop: 12 }]}>{isNetwork ? '9. Tools & Approach' : '10. Tools & Approach'}</Text>
      <View style={styles.titleUnderlineRow}>
        <View style={styles.titleUnderlineBlue} />
        <View style={styles.titleUnderlineDark} />
      </View>

      <Text style={styles.blueSubtitle}>Approach</Text>
      <Text style={styles.paragraph}>
        {approachText}
      </Text>

      <Text style={styles.blueSubtitle}>Tools used</Text>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={[styles.tableHeaderCell, { width: '30%' }]}>TOOL</Text>
          <Text style={[styles.tableHeaderCell, { width: '70%' }]}>PURPOSE</Text>
        </View>
        {tools.map((t: any, idx: number) => {
          const isLast = idx === tools.length - 1;
          return (
            <View key={idx} style={[styles.tableRow, { borderBottomWidth: isLast ? 0 : 1 }]}>
              <Text style={[styles.tableRowCell, { width: '30%', color: '#1F2937' }]}>
                {t.name || t.tool}
              </Text>
              <Text style={[styles.tableRowCell, { width: '70%', color: '#1F2937' }]}>
                {t.purpose}
              </Text>
            </View>
          );
        })}
      </View>

      {/* Footer */}
      <View style={styles.footer} fixed>
        <Text style={styles.footerLeft}>Capture The Bug - Confidential</Text>
        <Text style={styles.footerRight} render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
      </View>
    </Page>
  );
};

export default ConclusionToolsPageV2;
